import { useLang } from "@/App";
import { all, pick } from "@/data/images";
import Hero from "@/components/Hero";
import Foto from "@/components/Foto";
import { Reveal, Pretitulo } from "@/components/Reveal";
import { useLightbox } from "@/hooks/useLightbox";

export default function QuienesSomos() {
  const { t } = useLang();
  const p = t.quienesSomos;
  const { open, portal } = useLightbox();
  const retratoFotos = p.dueños.retratos.map((r) => pick(r.foto[0], 0) || all(r.foto[0])[0]);
  const anecdotaFotos = p.anecdotas.cards.map((c, i) => pick(c.foto[0], c.foto[1]) || all(c.foto[0])[i]);

  return (
    <div data-testid="pagina-quienes-somos">
      <Hero pre={p.hero.pre} titulo={p.hero.titulo} bajada={p.hero.bajada} fotos={[pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0]]} testid="hero-quienes-somos" foco="center 8%" />

      {/* 2. Origen */}
      <section className="py-20 md:py-28" data-testid="qs-origen">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[6fr_5fr] lg:gap-20">
          <Reveal>
            <Pretitulo center={false}>{p.origen.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-4xl">{p.origen.titulo}</h2>
            <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-ink-soft">{p.origen.cuerpo}</p>
          </Reveal>
          <Reveal delay={0.12} className="img-frame aspect-[4/3] w-full">
            <Foto src={pick(p.origen.foto[0], 0) || all(p.origen.foto[0])[0]} alt="" className="h-full w-full" testid="qs-origen-foto" />
          </Reveal>
        </div>
      </section>

      {/* 3. Camino (composición invertida) */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid="qs-camino">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[5fr_6fr] lg:gap-20">
          <Reveal className="img-frame order-2 aspect-[4/3] w-full lg:order-1">
            <Foto src={pick(p.camino.foto[0], 0) || all(p.camino.foto[0])[0]} alt="" className="h-full w-full" testid="qs-camino-foto" />
          </Reveal>
          <Reveal delay={0.12} className="order-1 lg:order-2">
            <Pretitulo center={false}>{p.camino.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-4xl">{p.camino.titulo}</h2>
            <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-ink-soft">{p.camino.cuerpo}</p>
          </Reveal>
        </div>
      </section>

      {/* 4. Frase destacada */}
      <section className="py-24 md:py-36" data-testid="qs-frase">
        <Reveal className="mx-auto max-w-3xl px-6 text-center md:px-10">
          <p className="font-serif text-3xl font-light italic leading-snug text-ink md:text-4xl">{p.frase.texto}</p>
          <p className="mt-7 text-[11px] font-bold tracking-[0.3em] text-terra">— {p.frase.firma}</p>
        </Reveal>
      </section>

      {/* 5. Diego y Marcela */}
      <section className="pb-20 md:pb-28" data-testid="qs-duenos">
        <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[6fr_4fr] lg:gap-20">
          <Reveal>
            <Pretitulo center={false}>{p.dueños.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-4xl">{p.dueños.titulo}</h2>
            <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-ink-soft">{p.dueños.cuerpo}</p>
          </Reveal>
          <div className="grid grid-cols-2 gap-5">
            {p.dueños.retratos.map((r, i) => (
              <Reveal key={r.nombre} delay={i * 0.1} testid={`qs-retrato-${i}`}>
                <button type="button" onClick={() => open(retratoFotos, i)} className="img-frame block aspect-[3/4] w-full cursor-pointer" data-testid={`qs-retrato-foto-${i}-btn`}>
                  <Foto src={retratoFotos[i]} alt={r.nombre} className="h-full w-full" testid={`qs-retrato-foto-${i}`} />
                </button>
                <p className="mt-4 font-serif text-xl text-ink">{r.nombre}</p>
                <p className="mt-1 text-[13px] leading-relaxed text-ink-soft">{r.texto}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Anécdotas */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid="qs-anecdotas">
        <div className="mx-auto max-w-[1300px] px-6 md:px-10">
          <Reveal className="text-center">
            <Pretitulo>{p.anecdotas.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">{p.anecdotas.titulo}</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3 md:gap-6">
            {p.anecdotas.cards.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08} className="flex flex-col" testid={`qs-anecdota-${i}`}>
                <button type="button" onClick={() => open(anecdotaFotos, i)} className="img-frame block aspect-[4/3] w-full cursor-pointer" data-testid={`qs-anecdota-foto-${i}-btn`}>
                  <Foto src={anecdotaFotos[i]} alt="" className="h-full w-full" testid={`qs-anecdota-foto-${i}`} />
                </button>
                <h3 className="mt-6 font-serif text-2xl font-light text-ink">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      {portal}
    </div>
  );
}